// LibraryPage.jsx
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";
import AffiliateLinks from "./../modules/amazonAffiliate/AffiliateLinks";

const LibraryPage = () => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      const threadsCollection = collection(db, "stories");
      const querySnapshot = await getDocs(threadsCollection);
      const cats = new Set();
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.category) cats.add(data.category);
      });
      setCategories(
        Array.from(cats).map((cat, idx) => ({ id: idx, name: cat }))
      );
    };
    fetchCategories();
  }, []);

  // --- URLパラメータ取得 ---
  const searchParams = new URLSearchParams(location.search);
  const yearParam = searchParams.get("year");
  const monthParam = searchParams.get("month");
  const categoryParam = searchParams.get("category");
  const searchParam = searchParams.get("search"); // 🔍追加：検索ワード取得
  const year = yearParam ? Number(yearParam) : null;
  const month = monthParam ? Number(monthParam) : null;

  // --- 元スレットIDを取得する関数 ---
  const getRootStoryId = async (storyId) => {
    let currentId = storyId;
    while (currentId) {
      const docRef = doc(db, "stories", currentId);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) break;
      const data = docSnap.data();
      if (!data.baseStoryId) return currentId;
      currentId = data.baseStoryId;
    }
    return storyId;
  };

  // --- スレッド取得 ---
  useEffect(() => {
    const fetchThreads = async () => {
      setLoading(true);
      try {
        const threadsCollection = collection(db, "stories");
        const threadsQuery = query(
          threadsCollection,
          orderBy("createdAt", "desc")
        );
        const querySnapshot = await getDocs(threadsQuery);

        const fetchedThreads = await Promise.all(
          querySnapshot.docs.map(async (docSnap) => {
            const data = docSnap.data();
            let firstNodeId = data.firstNodeId || null;

            // 最初のノード取得
            if (!firstNodeId) {
              const nodesQuery = query(
                collection(db, "stories", docSnap.id, "nodes"),
                orderBy("createdAt", "asc"),
                limit(1)
              );
              const nodesSnapshot = await getDocs(nodesQuery);
              if (!nodesSnapshot.empty) firstNodeId = nodesSnapshot.docs[0].id;
            }

            return {
              id: docSnap.id,
              title: data.title,
              category: data.category || "未分類",
              firstNodeId,
              voteMethod: data.voteMethod,
              voteTime: data.voteTime,
              createdAt: data.createdAt,
              updatedAt: data.updatedAt,
            };
          })
        );

        setThreads(fetchedThreads);
      } catch (err) {
        console.error("スレッド取得失敗:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchThreads();
  }, [year, month, categoryParam, searchParam]);

  // --- 絞り込み ---
  const filtered = threads.filter((thread) => {
    if (!thread.createdAt) return false;
    const date = thread.createdAt.toDate();

    if (year && date.getFullYear() !== year) return false;
    if (month && date.getMonth() + 1 !== month) return false;
    if (categoryParam && thread.category !== categoryParam) return false;

    // 🔍 タイトル検索のみ
    if (searchParam) {
      const keyword = searchParam.toLowerCase();
      const titleMatch = thread.title?.toLowerCase().includes(keyword);
      if (!titleMatch) return false;
    }

    return true;
  });

  // --- カテゴリごとにグループ化 ---
  const categorizedThreads = filtered.reduce((acc, thread) => {
    if (!acc[thread.category]) acc[thread.category] = [];
    acc[thread.category].push(thread);
    return acc;
  }, {});

  // --- 表示 ---
  if (loading) return <p>読み込み中...</p>;
  if (filtered.length === 0) return <p>スレッドが見つかりません</p>;

  return (
    <div className="library-container">
      <h1>スレッドを検索</h1>

      {/* カテゴリボタングリッド */}
      <div className="library-box">
        <h2>カテゴリで検索</h2>
        <div className="category-grid">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/library?category=${cat.name}`}
              className="category-button"
            >
              {cat.name}
            </Link>
          ))}
        </div>
      </div>
          
      <div className="affiliate-group home-section">
        {/* 🔹 AffiliateLinks を表示 */}
        <AffiliateLinks />
      </div>

      {/* スレッドリスト */}
      <div className="library-sections">
        <div className="library-box main">
          {searchParam && <p>検索結果：「{searchParam}」</p>}
          {Object.keys(categorizedThreads).map((category) => (
            <div key={category} className="thread-category">
              <h3>{category}</h3>
              <ul>
                {categorizedThreads[category].map((thread) => (
                  <li
                    key={thread.id}
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      justifyContent: "space-between", // ← タイトルとボタンを左右に
                    }}
                  >
                    <span
                      style={{
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Link
                        to={`/chat/${thread.firstNodeId}`}
                        state={{
                          storyId: thread.id,
                          nodeId: thread.firstNodeId,
                        }}
                        style={{ textDecoration: "none", color: "#222" }}
                      >
                        {thread.title}
                      </Link>
                    </span>

                    <button
                      className="tree-button"
                      onClick={async () => {
                        const rootStoryId = await getRootStoryId(thread.id);
                        navigate(`/storytree/${rootStoryId}`, {
                          state: { storyId: rootStoryId },
                        });
                      }}
                    >
                      マインドマップへ
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
                    <div className="affiliate-group home-section">
                {/* 🔹 AffiliateLinks を表示 */}
                <AffiliateLinks />
              </div>
    </div>
  );
};

export default LibraryPage;
